import { useState, useEffect } from 'react'
import api from '../../services/api'
import { useTranslation } from 'react-i18next'

/** Self-service leave (v3.4) — any user can view balances, request time off, and cancel. */
const TYPES = ['Annual', 'Sick', 'Unpaid', 'Personal']
const STATUS_STYLE = {
  Pending: 'bg-amber-100 text-amber-700', Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-red-100 text-red-700', Cancelled: 'bg-gray-100 text-gray-500',
}

export default function MyLeave() {
  const { t } = useTranslation('ops')
  const { t: tc } = useTranslation('common')
  const [data, setData] = useState({ requests: [], balances: [] })
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ leave_type: 'Annual', start_date: '', end_date: '', reason: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/hr/my/leave').then(r => setData(r.data || { requests: [], balances: [] })).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const submit = async () => {
    setError('')
    if (!form.start_date || !form.end_date) { setError('Pick a start and end date.'); return }
    setBusy(true)
    try {
      await api.post('/hr/my/leave', form)
      setForm({ leave_type: 'Annual', start_date: '', end_date: '', reason: '' })
      load()
    } catch (e) { setError(e.response?.data?.detail || 'Could not submit the request.') }
    finally { setBusy(false) }
  }

  const cancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return
    try { await api.post(`/hr/my/leave/${id}/cancel`); load() }
    catch (e) { alert(e.response?.data?.detail || 'Could not cancel.') }
  }

  const days = (form.start_date && form.end_date)
    ? Math.max(0, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1) : 0

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('my_leave')}</h1>
        <p className="text-gray-500 text-sm mt-1">Request time off and track your balances. Your manager (or HR) approves it.</p>
      </div>

      {data.balances.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {data.balances.map(b => (
            <div key={b.leave_type} className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{b.leave_type} {b.year}</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{Math.max(0, (b.entitled_days || 0) - (b.used_days || 0))} <span className="text-sm font-normal text-gray-500">left</span></p>
              <p className="text-[11px] text-gray-400">{b.used_days || 0} used / {b.entitled_days || 0}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">{t('request_time_off')}</h3>
        {error && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{tc('labels.category')}</label>
            <select value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('from')}</label>
            <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={submit} disabled={busy}
              className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {busy ? 'Submitting…' : `Request${days ? ` (${days}d)` : ''}`}
            </button>
          </div>
        </div>
        <input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
          placeholder={t('ph_reason_optional')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-3" />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">{t('my_requests')}</h3>
        {loading ? <p className="text-sm text-gray-400">{t('loading')}</p>
          : data.requests.length === 0 ? <p className="text-sm text-gray-400 italic">{t('no_leave_requests')}</p>
          : (
            <div className="divide-y divide-gray-100">
              {data.requests.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{r.leave_type} · {r.days}d</p>
                    <p className="text-xs text-gray-500">{r.start_date} → {r.end_date}{r.reason ? ` · ${r.reason}` : ''}</p>
                    {r.decision_note && <p className="text-[11px] text-gray-400 italic">Note: {r.decision_note}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                    {['Pending', 'Approved'].includes(r.status) && (
                      <button onClick={() => cancel(r.id)} className="text-[11px] text-gray-500 hover:text-red-600">{tc('actions.cancel')}</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  )
}
